import React, { useEffect, useMemo, useState } from 'react';
import authAxios from '../utils/authAxios';
import StandardTableLayout from '../styles/StandardTableLayout';
import { Input, Button, DangerButton, Select, SecondaryButton } from '../styles/TableStyles';
import { savedToast, errorToast, deletedToast } from '../utils/toastUtils';

const initialForm = { GradeName: '', Commodity: '' };

const GradesPage = () => {
  const [rows, setRows] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [formData, setFormData] = useState(initialForm);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const collator = useMemo(() => new Intl.Collator(undefined, { sensitivity: 'base' }), []);

  const fetchRows = async () => {
    try {
      const res = await authAxios.get('/api/grades');
      setRows(res.data);
    } catch (e) { console.error('Fetch grades failed', e); }
  };

  const fetchCommodities = async () => {
    try {
      const res = await authAxios.get('/api/commodities/dropdown');
      setCommodities(res.data || []);
    } catch (e) { console.error('Fetch commodities failed', e); }
  };

  useEffect(() => { fetchRows(); fetchCommodities(); }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.GradeName?.trim()) return alert('Grade Name is required');
    if (formData.GradeName.includes(',')) return alert('Grade Name cannot contain a comma');
    try {
      setSubmitting(true);
      if (isEditing) {
        await authAxios.put(`/api/grades/${editingId}`, formData);
      } else {
        await authAxios.post('/api/grades', formData);
      }
      setFormData(initialForm);
      setShowForm(false);
      setIsEditing(false);
      setEditingId(null);
      fetchRows();
      savedToast('Grade');
    } catch (e) { console.error('Submit failed', e); errorToast(); }
    finally { setSubmitting(false); }
  };

  const handleEdit = (row) => {
    setFormData({ GradeName: row.GradeName ?? '', Commodity: row.Commodity ?? '' });
    setEditingId(row.UniqueID);
    setIsEditing(true);
    setShowForm(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this grade?')) return;
    try {
      await authAxios.delete(`/api/grades/${id}`);
      fetchRows();
      deletedToast('Grade');
    } catch (e) { console.error('Delete failed', e); }
  };

  const columns = [ { key: 'CommodityName', label: 'Commodity' }, { key: 'GradeName', label: 'Grade Name' } ];

  const sorted = useMemo(() => (
    [...rows].sort((a, b) => {
      const c = collator.compare(a.CommodityName ?? '', b.CommodityName ?? '');
      if (c !== 0) return c;
      return collator.compare(a.GradeName ?? '', b.GradeName ?? '');
    })
  ), [rows, collator]);

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return sorted.filter(r => (r.GradeName || '').toLowerCase().includes(q));
  }, [sorted, searchQuery]);

  return (
    <StandardTableLayout
      title="Grades"
      showForm={showForm}
      submitting={submitting}
      onToggleForm={() => { setShowForm(!showForm); setFormData(initialForm); setIsEditing(false); setEditingId(null); }}
      onSubmit={handleSubmit}
      columns={columns}
      formData={formData}
      handleChange={handleChange}
      rows={filtered}
      editingId={editingId}
      searchValue={searchQuery}
      onSearchChange={(e) => setSearchQuery(e.target.value)}
      renderCell={(col, data, handleChange) => {
        if (!handleChange) {
          return (
            <span style={{ background: 'transparent', display: 'inline-block', padding: '4px 0' }}>
              {String(data?.[col] ?? '')}
            </span>
          );
        }
        return (
          col === 'CommodityName' ? (
            <Select name="Commodity" value={data.Commodity ?? ''} onChange={handleChange}>
              <option value="">Select commodity</option>
              {commodities.map(c => (
                <option key={c.UniqueID ?? c.Commodity} value={c.UniqueID}>{c.Commodity}</option>
              ))}
            </Select>
          ) : (
            <Input type="text" name="GradeName" value={data.GradeName ?? ''} onChange={handleChange} />
          )
        );
      }}
      renderActions={(row) => (
        editingId === row.UniqueID ? (
          <>
            <Button type="submit" disabled={submitting}>Save</Button>
            <SecondaryButton type="button" onClick={() => { setIsEditing(false); setEditingId(null); setFormData(initialForm); }}>Cancel</SecondaryButton>
          </>
        ) : (
          <>
            <SecondaryButton type="button" onClick={() => handleEdit(row)}>Edit</SecondaryButton>
            <DangerButton type="button" onClick={() => handleDelete(row.UniqueID)}>Delete</DangerButton>
          </>
        )
      )}
    />
  );
};

export default GradesPage;


